import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import api from '../utils/firebase/api';
import { WellHeader } from './well-detail/WellHeader';
import { WellStatsGrid } from './well-detail/WellStatsGrid';
import { WellStatusCard } from './well-detail/WellStatusCard';
import { WellInfoCard } from './well-detail/WellInfoCard';
import { RecentEntriesCard } from './well-detail/RecentEntriesCard';
import { WellCharts } from './well-detail/WellCharts';
import { WellNotesTab } from './well-detail/WellNotesTab';
import { MaintenanceModal } from './well-detail/MaintenanceModal';
import PhotoGallery, { Photo } from './PhotoGallery';
import ComprehensiveWellForm from './ComprehensiveWellForm';
import JumpToDateModal from './JumpToDateModal';
import TagsModal from './TagsModal';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs'; // Assuming we have standard Tabs ui
import { Badge } from './ui/badge';
import { cn } from './ui/utils';

// Types
interface WellNote {
    id: string;
    text: string;
    type: 'location' | 'maintenance' | 'general';
    createdAt: string;
    createdBy: string;
}

interface WellEntry {
    id?: string;
    timestamp: string;
    oilProduction: number;
    waterProduction: number;
    staticPressure?: number;
    dropPressure?: number;
    notes?: string;
}

interface WellDetailProps {
    well: any;
    wells?: any[];
    onDataEntry: () => void;
    onAutoSaveDataEntry?: () => void;
    onRefresh: () => void;
    onUpdateWell?: (wellId: string, updates: any) => void;
    onBack: () => void;
    onWellChange?: (well: any) => void;
    onNavigateToWell?: (well: any) => void;
    user?: any;
    onNavigateToServiceTickets?: (prefillData?: any) => void;
    isDarkMode?: boolean;
    /** Optional calculated production values */
    calculatedProduction?: {
        netOilBBL: number;
        grossWaterBBL: number;
        waterCutPercent: number;
    };
}

export default function WellDetail({
    well,
    wells,
    onDataEntry,
    onAutoSaveDataEntry,
    onRefresh,
    onUpdateWell,
    onBack,
    onWellChange,
    onNavigateToWell,
    user,
    onNavigateToServiceTickets,
    isDarkMode = false,
    calculatedProduction
}: WellDetailProps) {
    const [activeTab, setActiveTab] = useState('overview');

    // State logic from original
    const [notes, setNotes] = useState<WellNote[]>([]);
    const [showEditor, setShowEditor] = useState(false);
    const [showDatePicker, setShowDatePicker] = useState(false);
    const [showTags, setShowTags] = useState(false);
    const [wellPhotos, setWellPhotos] = useState<Photo[]>([]); // Assuming PhotoGallery manages its own fetch normally, but original had it passed
    const [selectedDate, setSelectedDate] = useState(new Date());

    // Maintenance state
    const [showMaintenanceModal, setShowMaintenanceModal] = useState(false);

    // Production entries - support both property names for backward compatibility
    // PumpersPortal sets `productionEntries`, legacy code may use `entries`
    const entries: WellEntry[] = well.entries || well.productionEntries || [];

    useEffect(() => {
        loadNotes();
    }, [well.id]);

    const loadNotes = async () => {
        try {
            const companyId = well.companyId || user?.companyId || (user as any)?.companyId;
            if (companyId && well.id) {
                const fetchedNotes = await api.fetchWellNotes(companyId, well.id);
                setNotes(fetchedNotes as WellNote[]);
            }
        } catch (error) {
            console.error('Failed to load notes', error);
        }
    };

    const handleUpdateStatus = (newStatus: string) => {
        if (onUpdateWell) {
            onUpdateWell(well.id, { status: newStatus });
        }
        toast.success(`Status updated to ${newStatus}`);
        onRefresh();
    };

    const handleMaintenanceSubmit = async (data: { reason: string; notes: string; createTicket: boolean }) => {
        const maintenanceInfo = {
            status: 'maintenance',
            maintenanceTicket: {
                id: `maint-${Date.now()}`,
                ticketNumber: `MAINT-${Date.now().toString().slice(-6)}`,
                title: data.reason,
                description: data.notes,
                serviceType: 'maintenance',
                startDate: new Date().toISOString(),
                reportedBy: user?.name || user?.email || 'Field Worker',
                reportedById: user?.id,
            }
        };

        if (onUpdateWell) {
            onUpdateWell(well.id, maintenanceInfo);
        }

        // Simulating notification logic from original
        toast.success('Well set to maintenance mode', { description: 'Admin has been notified.' });

        if (data.createTicket && onNavigateToServiceTickets) {
            onNavigateToServiceTickets({
                wellId: well.id,
                wellName: well.name,
                ticketType: 'maintenance',
                priority: 'high',
                title: data.reason,
                description: data.notes,
                reportedBy: user?.name || user?.email,
                reportedById: user?.id,
            });
        }

        setShowMaintenanceModal(false);
        onRefresh();
    };

    const handleAddNote = async (text: string) => {
        try {
            const companyId = well.companyId || user?.companyId || (user as any)?.companyId;
            if (!companyId) {
                toast.error('Cannot save note: Missing tenant context');
                return;
            }

            const noteData = {
                text,
                type: 'general',
                createdBy: user?.name || user?.email || 'User',
                createdById: user?.id
            };

            await api.createWellNote(companyId, well.id, noteData);
            toast.success('Note saved');
            loadNotes(); // Refresh list
        } catch (error) {
            console.error(error);
            toast.error('Failed to save note');
        }
    };

    return (
        <div className={cn("flex flex-col h-full bg-slate-50", isDarkMode ? "bg-slate-950" : "")}>
            {/* Header */}
            <WellHeader
                well={well}
                wells={wells || []}
                onBack={onBack}
                onEdit={() => setShowEditor(true)}
                onNavigate={() => onNavigateToWell && onNavigateToWell(well)}
                onDataEntry={onDataEntry}
                onAutoSaveEntry={onAutoSaveDataEntry}
                onWellChange={onWellChange}
                onOpenDatePicker={() => setShowDatePicker(true)}
                onOpenTags={() => setShowTags(true)}
                isDarkMode={isDarkMode}
            />

            {/* Content with Scroll */}
            <div className="flex-1 overflow-y-auto">
                <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
                    {/* Stats Grid - Use calculated values when available */}
                    <WellStatsGrid
                        oil={calculatedProduction?.netOilBBL ?? well.oilProduction ?? 0}
                        water={calculatedProduction?.grossWaterBBL ?? well.waterProduction ?? 0}
                        gas={well.staticPressure || 0}
                        isDarkMode={isDarkMode}
                        isCalculated={!!calculatedProduction}
                    />

                    {/* Tabs / Segmented Control */}
                    <div className="flex justify-center mb-6">
                        <div className={cn(
                            "inline-flex p-1 rounded-xl border",
                            isDarkMode ? "bg-slate-900 border-slate-800" : "bg-white border-gray-200"
                        )}>
                            {['overview', 'entries', 'charts', 'photos', 'notes'].map(tab => (
                                <button
                                    key={tab}
                                    onClick={() => setActiveTab(tab)}
                                    className={cn(
                                        "px-4 py-2 rounded-lg text-sm font-medium capitalize transition-all",
                                        activeTab === tab
                                            ? isDarkMode ? "bg-slate-800 text-white shadow-sm" : "bg-slate-100 text-slate-900 shadow-sm"
                                            : isDarkMode ? "text-slate-400 hover:text-slate-200" : "text-slate-500 hover:text-slate-900"
                                    )}
                                >
                                    {tab}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Tab Content */}
                    <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
                        {activeTab === 'overview' && (
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                <section className="lg:col-span-2 space-y-6">
                                    <WellCharts entries={entries} isDarkMode={isDarkMode} />
                                    <RecentEntriesCard
                                        entries={entries}
                                        onAddEntry={onDataEntry}
                                        isDarkMode={isDarkMode}
                                        onViewAll={() => setActiveTab('entries')}
                                    />
                                </section>
                                <section className="space-y-6">
                                    <WellStatusCard
                                        status={well.status || 'active'}
                                        maintenanceTicket={well.maintenanceTicket}
                                        onUpdateStatus={handleUpdateStatus}
                                        onRequestMaintenance={() => setShowMaintenanceModal(true)}
                                        isDarkMode={isDarkMode}
                                    />
                                    <WellInfoCard well={well} isDarkMode={isDarkMode} />

                                    {/* Recent Notes Preview */}
                                    <div className={cn("p-4 rounded-xl border space-y-3", isDarkMode ? "bg-slate-900 border-slate-800" : "bg-white border-slate-200")}>
                                        <div className="flex items-center justify-between">
                                            <h3 className={cn("text-sm font-semibold", isDarkMode ? "text-white" : "text-slate-900")}>Recent Field Notes</h3>
                                            <button onClick={() => setActiveTab('notes')} className="text-xs text-blue-500 hover:underline">View All</button>
                                        </div>
                                        <div className="space-y-3">
                                            {notes.slice(0, 3).map(note => (
                                                <div key={note.id} className="text-sm border-b border-dashed border-slate-200 dark:border-slate-800 last:border-0 pb-2 last:pb-0">
                                                    <div className="flex justify-between text-[10px] text-muted-foreground mb-1 uppercase tracking-wide">
                                                        <span>{new Date(note.createdAt).toLocaleDateString()}</span>
                                                        <span className={cn(
                                                            note.type === 'maintenance' ? "text-amber-500" : "text-blue-500"
                                                        )}>{note.type}</span>
                                                    </div>
                                                    <p className={cn("line-clamp-2 text-xs leading-relaxed", isDarkMode ? "text-slate-400" : "text-slate-600")}>{note.text}</p>
                                                </div>
                                            ))}
                                            {notes.length === 0 && <p className="text-xs text-muted-foreground italic">No notes recorded.</p>}
                                        </div>
                                    </div>
                                </section>
                            </div>
                        )}

                        {activeTab === 'entries' && (
                            <div className="space-y-4">
                                <h3 className={cn("text-lg font-semibold px-1", isDarkMode ? "text-white" : "text-slate-900")}>Full Entry History</h3>
                                {entries.length === 0 ? (
                                    <RecentEntriesCard entries={[]} onAddEntry={onDataEntry} isDarkMode={isDarkMode} />
                                ) : (
                                    <div className="grid gap-4">
                                        {/* Using Reuse of RecentEntriesCard logic or mapping simpler cards */}
                                        {entries.map(entry => (
                                            <div key={entry.id} className={cn("p-4 rounded-xl border", isDarkMode ? "bg-slate-900 border-slate-800" : "bg-white border-slate-200")}>
                                                <div className="flex justify-between items-start mb-2">
                                                    <div>
                                                        <div className="font-semibold">{new Date(entry.timestamp).toLocaleDateString()}</div>
                                                        <div className="text-xs text-muted-foreground">{new Date(entry.timestamp).toLocaleTimeString()}</div>
                                                    </div>
                                                    <Badge variant="outline">#{entry.id}</Badge>
                                                </div>
                                                <div className="grid grid-cols-4 gap-4 mt-2">
                                                    <div><div className="text-xs uppercase text-muted-foreground">Oil</div><div className="font-mono">{Math.round(entry.oilProduction)}</div></div>
                                                    <div><div className="text-xs uppercase text-muted-foreground">Water</div><div className="font-mono">{Math.round(entry.waterProduction)}</div></div>
                                                    <div><div className="text-xs uppercase text-muted-foreground">Gas</div><div className="font-mono">{Math.round(entry.staticPressure || 0)}</div></div>
                                                    <div><div className="text-xs uppercase text-muted-foreground">PSI</div><div className="font-mono">{Math.round(entry.dropPressure || 0)}</div></div>
                                                </div>
                                                {entry.notes && <div className="mt-3 text-sm bg-muted/50 p-2 rounded">{entry.notes}</div>}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {activeTab === 'charts' && (
                            <WellCharts entries={entries} isDarkMode={isDarkMode} />
                        )}

                        {activeTab === 'photos' && (
                            <PhotoGallery
                                photos={wellPhotos}
                                onAddPhoto={(photo: Photo) => setWellPhotos([...wellPhotos, photo])}
                                onPhotoDelete={(photoId: string) => setWellPhotos(wellPhotos.filter(p => p.id !== photoId))}
                                allowDelete={true}
                            />
                        )}

                        {activeTab === 'notes' && (
                            <WellNotesTab
                                notes={notes}
                                onAddNote={handleAddNote}
                                isDarkMode={isDarkMode}
                            />
                        )}
                    </div>
                </div>
            </div>

            {/* Modals */}
            <MaintenanceModal
                isOpen={showMaintenanceModal}
                onClose={() => setShowMaintenanceModal(false)}
                onSubmit={handleMaintenanceSubmit}
                wellName={well.name}
                isDarkMode={isDarkMode}
            />

            {showEditor && (
                <ComprehensiveWellForm
                    well={well}
                    onClose={() => setShowEditor(false)}
                    onSave={() => {
                        setShowEditor(false);
                        onRefresh();
                    }}
                    isDarkMode={isDarkMode}
                />
            )}

            <JumpToDateModal
                isOpen={showDatePicker}
                onClose={() => setShowDatePicker(false)}
                onSelectDate={(date) => {
                    setSelectedDate(date);
                    toast.success(`Broadcasting jump to ${date.toLocaleDateString()}`);
                    // Logic to scroll to date would typically be implemented here
                }}
                initialDate={selectedDate}
                entriesMap={new Map(entries.map(e => [new Date(e.timestamp).toISOString().split('T')[0], 1]))}
            />

            <TagsModal
                isOpen={showTags}
                onClose={() => setShowTags(false)}
                currentTags={well.tags || []}
                onSaveTags={async (tags) => {
                    if (onUpdateWell) {
                        try {
                            await onUpdateWell(well.id, { tags });
                            toast.success('Tags updated');
                        } catch (e) {
                            toast.error('Failed to update tags');
                        }
                    }
                }}
                wellName={well.name}
            />

        </div>
    );
}
